import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';

import { BackgroundCheckTypes, RosterParticipant } from '../../../models';
import { GroupManagementService } from '@services/group-management.service';
import { PanelHostService } from '@services/panel-host.service';
import { rosterParticipantFromPanelContext } from '@services/panel-store';
import { TripService } from '@services/trip.service';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';
import { participantBackgroundCheckWaivedOption } from './participant-background-check-waived-option';

@Component({
  selector: 'app-participant-background-check-panel',
  imports: [FormsModule, TranslocoDirective, ContentContainerComponent, PanelBodyComponent],
  templateUrl: './participant-background-check-panel.component.html',
  styleUrls: ['./participant-background-check-panel.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantBackgroundCheckPanelComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripService = inject(TripService);
  private readonly groupManagementService = inject(GroupManagementService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  readonly submitting = signal(false);
  readonly backgroundCheckType = signal<BackgroundCheckTypes>(BackgroundCheckTypes.SelfManaged);
  readonly backgroundCheckDate = signal<string>('');
  readonly maxBackgroundCheckDate = DateTime.now().toFormat('yyyy-MM-dd');

  readonly participant = computed((): RosterParticipant | null =>
    rosterParticipantFromPanelContext(this.panelHost.rosterPanelContext()),
  );

  readonly trip = computed(() => this.tripService.currentTrip.value());

  readonly showSelfManagedOptions = computed(() => {
    const trip = this.trip();
    if (!trip) {
      return false;
    }
    const tripType = trip.backgroundCheckType ?? BackgroundCheckTypes.None;
    return tripType === BackgroundCheckTypes.SelfManaged || tripType === BackgroundCheckTypes.Mixed;
  });

  readonly showSterlingOption = computed(() => {
    const trip = this.trip();
    if (!trip) {
      return false;
    }
    const tripType = trip.backgroundCheckType ?? BackgroundCheckTypes.None;
    return tripType === BackgroundCheckTypes.Sterling || tripType === BackgroundCheckTypes.Mixed;
  });

  readonly waivedOption = computed(() => {
    const participant = this.participant();
    if (!participant) {
      return { visible: false, disabled: false };
    }
    return participantBackgroundCheckWaivedOption(participant);
  });

  readonly showDateField = computed(
    () => this.backgroundCheckType() === BackgroundCheckTypes.SelfManaged,
  );

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() !== 'background-check') {
        return;
      }

      const participant = this.participant();
      if (!participant) {
        return;
      }

      const ntu = participant.newtripUser;
      this.backgroundCheckType.set(ntu.backgroundCheckType ?? BackgroundCheckTypes.SelfManaged);
      this.backgroundCheckDate.set(formatDateInput(ntu.backgroundCheckDate));
      this.submitting.set(false);
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  save(): void {
    const participant = this.participant();
    const trip = this.trip();
    if (!participant || !trip || this.submitting()) {
      return;
    }

    const backgroundCheckType = this.backgroundCheckType();
    const backgroundCheckDate =
      backgroundCheckType === BackgroundCheckTypes.SelfManaged
        ? this.backgroundCheckDate() || null
        : null;

    if (
      backgroundCheckType === BackgroundCheckTypes.SelfManaged &&
      backgroundCheckDate &&
      backgroundCheckDate > this.maxBackgroundCheckDate
    ) {
      void this.showFutureDateError();
      return;
    }

    this.submitting.set(true);
    this.groupManagementService
      .updateParticipantBackgroundCheck({
        tripId: trip.id,
        userId: participant.id,
        backgroundCheckType,
        backgroundCheckDate,
        backgroundCheckProvider: participant.newtripUser.backgroundCheckProvider,
      })
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              marker('group-management.panels.background-check.success'),
              { name: participant.firstname },
            ),
            duration: 4000,
            color: 'success',
          });
          await toast.present();
          this.panelHost.dismiss();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              marker('group-management.panels.background-check.error'),
              { name: participant.firstname },
            ),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }

  private async showFutureDateError(): Promise<void> {
    const toast = await this.toastController.create({
      message: this.transloco.translate(
        marker('group-management.panels.background-check.date-future-error'),
      ),
      duration: 4000,
      color: 'danger',
    });
    await toast.present();
  }
}

function formatDateInput(value: Date | string | null | undefined): string {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return value.slice(0, 10);
  }
  return DateTime.fromJSDate(value).toFormat('yyyy-MM-dd');
}
